import React from 'react';
import { motion } from 'framer-motion';

// ---- Data -----------------------------------------------------------------

// Real, peer-reviewed sources. Each is general research on Cordyceps / cordycepin
// — NOT a claim about Cordinal's specific products. Evidence level is labelled honestly.
const studies = [
  {
    level: 'Human RCT · C. militaris',
    title: 'Exercise tolerance & VO₂max',
    body: 'In a randomized, double-blind, placebo-controlled trial, 1–3 weeks of a Cordyceps militaris–containing supplement improved tolerance to high-intensity exercise and maximal oxygen uptake (VO₂max) in healthy adults.',
    source: 'Hirsch et al., Journal of Dietary Supplements (2017)',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC5236007/'
  },
  {
    level: 'Human RCT · Cs-4',
    title: 'Metabolic & ventilatory threshold',
    body: 'A double-blind, placebo-controlled trial in older adults showed a 10.5% increase in metabolic threshold and 8.5% increase in ventilatory threshold over a 12-week period, indicating better exercise performance and wellness.',
    source: 'Chen et al., J. Altern. Complement. Med. (2010)',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC3110835/'
  },
  {
    level: 'Review · C. militaris',
    title: 'Ergogenic & post-exercise recovery',
    body: 'A 2026 narrative review summarizes the growing human evidence for Cordyceps militaris supplementation on exercise performance and post-exercise recovery.',
    source: 'Nutrients 18(5):781 (2026)',
    url: 'https://www.mdpi.com/2072-6643/18/5/781'
  },
  {
    level: 'Review · Cordyceps',
    title: 'Ergogenic Aid: Does It Work?',
    body: 'A critical review providing a balanced appraisal of Cordyceps as an ergogenic aid, noting that evidence is promising but still emerging, and that chronic dosing over time is key for maximum benefit.',
    source: 'Current Nutrition Reports (2025)',
    url: 'https://link.springer.com/article/10.1007/s13668-025-00690-9'
  },
  {
    level: 'Preclinical · animal',
    title: 'Antioxidant & anti-fatigue',
    body: 'A 2025 animal study found cordycepin combined with antioxidants let subjects run longer while reducing fatigue markers (decreased lactate and creatine kinase). Evidence not yet confirmed in humans.',
    source: 'Scientific Reports (2025)',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11891301/'
  },
  {
    level: 'Preclinical · animal',
    title: 'Anti-fatigue mitochondrial pathways',
    body: 'An animal study showing that Cordycepin increased endurance running time at varying doses by acting on mitochondrial-energy pathways (TIGAR/SIRT1/PGC-1α).',
    source: 'Biochemical and Biophysical Research Communications (2022)',
    url: 'https://www.sciencedirect.com/science/article/abs/pii/S0006291X22015443'
  },
  {
    level: 'Review · Mechanism',
    title: 'Pharmacological mechanisms',
    body: 'A comprehensive review mapping out how cordycepin structurally mimics adenosine to interact with the body\'s energy-sensing pathways, activating AMPK (the cell\'s energy switch) and influencing ATP signalling.',
    source: 'Tuli et al., 3 Biotech (2014)',
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3909570/'
  },
  {
    level: 'Review · Mechanism',
    title: 'Nutraceutical action mapping',
    body: 'A detailed molecular map of cordycepin\'s mechanisms of action, outlining its interaction with cellular energy pathways to support natural anti-fatigue and metabolic functions.',
    source: 'Ashraf et al., Molecules (2020)',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7356751/'
  }
];

const processSteps = [
  { n: '01', t: 'Cultivate', d: 'Grown on our own grain / rice substrate in a controlled room.' },
  { n: '02', t: 'Harvest', d: 'Whole orange fruiting bodies picked at maturity.' },
  { n: '03', t: 'Dry', d: 'Low-heat / sun-dried to preserve colour and compounds.' },
  { n: '04', t: 'Lab-test', d: 'Third-party NABL lab: heavy metals + microbiology, every batch.' }
];

// We test these every batch at a third-party NABL-accredited lab. We publish the
// parameters we screen, not the certificate itself — the full COA is available on request.
const labTested = {
  heavy: ['Lead (Pb)', 'Arsenic (As)', 'Cadmium (Cd)', 'Mercury (Hg)'],
  micro: ['Total Plate Count', 'Yeast & Mould', 'E. coli', 'Salmonella', 'P. aeruginosa', 'S. aureus'],
  quality: ['Moisture', 'Ash content', 'Water activity']
};

// ---- Animated molecule schematic ------------------------------------------

// An accurate skeletal structure of the nucleoside: an adenine base (fused
// purine — a 6-membered pyrimidine ring fused to a 5-membered imidazole ring)
// joined by a glycosidic bond at N9 to a β-D-ribofuranose sugar.
// `deoxy` toggles the single real difference between the two molecules: the 3′
// carbon carries –OH in adenosine and –H in cordycepin (3′-deoxyadenosine).
const BOND = '#c9c9c9';
const ERASE = '#060606'; // matches the card background, to knock out bonds under labels
const HL = '#f2680f';    // brand orange, used to spotlight the 3′ difference

// Adenine (purine) vertices
const C6 = [85, 50], N1 = [51, 70], C2 = [51, 110], N3 = [85, 130], C4 = [120, 110], C5 = [120, 70];
const N7 = [158, 58], C8 = [182, 90], N9 = [158, 122];
// Ribose (furanose) vertices
const C1p = [158, 165], C2p = [195, 188], C3p = [180, 228], C4p = [135, 228], O4p = [120, 188], C5p = [103, 246];
// Substituent label anchors
const NH2 = [85, 28], OH2 = [228, 176], OH3 = [200, 258], OH5 = [70, 238];

const L = (a, b, extra = {}) => (
  <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={BOND} strokeWidth="2" strokeLinecap="round" {...extra} />
);

const Atom = ({ p, children, fill = '#ffffff', r = 9, size = 13 }) => (
  <>
    <circle cx={p[0]} cy={p[1]} r={r} fill={ERASE} />
    <text x={p[0]} y={p[1]} fill={fill} fontSize={size} fontWeight="600"
          textAnchor="middle" dominantBaseline="central">{children}</text>
  </>
);

const Nucleoside = ({ deoxy }) => (
  <svg viewBox="0 0 250 280" width="100%" height="100%" role="img"
       aria-label={deoxy ? 'Cordycepin skeletal structure' : 'Adenosine skeletal structure'}>
    {/* --- Purine ring skeleton --- */}
    {L(C6, N1)}{L(N1, C2)}{L(C2, N3)}{L(N3, C4)}{L(C4, C5)}{L(C5, C6)}
    {L(C5, N7)}{L(N7, C8)}{L(C8, N9)}{L(N9, C4)}

    {/* aromatic double bonds (one Kekulé form) */}
    <line x1="81.8" y1="56.5" x2="58.2" y2="70.5" stroke={BOND} strokeWidth="2" />
    <line x1="58.2" y1="109.5" x2="81.8" y2="123.5" stroke={BOND} strokeWidth="2" />
    <line x1="116" y1="104" x2="116" y2="76" stroke={BOND} strokeWidth="2" />
    <line x1="158.4" y1="65.1" x2="175.2" y2="87.5" stroke={BOND} strokeWidth="2" />

    {/* 6-amino group + glycosidic bond to the sugar */}
    {L(C6, NH2)}
    {L(N9, C1p)}

    {/* --- Ribose sugar ring --- */}
    {L(C1p, C2p)}{L(C2p, C3p)}{L(C3p, C4p)}{L(C4p, O4p)}{L(O4p, C1p)}

    {/* 5′ and 2′ substituents (present in both molecules) */}
    {L(C4p, C5p)}{L(C5p, OH5)}
    {L(C2p, OH2)}

    {/* 3′ position — the single difference between the two molecules */}
    {deoxy && (
      <circle cx={C3p[0] + 12} cy={C3p[1] + 16} r="20" fill="none" stroke={HL}
              strokeWidth="1.6" className="mf-pulse" />
    )}
    <line x1={C3p[0]} y1={C3p[1]} x2={OH3[0]} y2={OH3[1]}
          stroke={deoxy ? HL : BOND} strokeWidth="2" strokeLinecap="round" />

    {/* Heteroatom + functional-group labels */}
    <Atom p={N1}>N</Atom>
    <Atom p={N3}>N</Atom>
    <Atom p={N7}>N</Atom>
    <Atom p={N9}>N</Atom>
    <Atom p={O4p}>O</Atom>
    <Atom p={NH2} r={12} size={12}>NH₂</Atom>
    <Atom p={OH2} r={11} size={12}>OH</Atom>
    <Atom p={OH5} r={12} size={12}>HO</Atom>
    <Atom p={OH3} r={11} size={12} fill={deoxy ? HL : '#ffffff'}>{deoxy ? 'H' : 'OH'}</Atom>

    {/* Locants */}
    <text x="196" y="150" fill="#666" fontSize="9">1′</text>
    <text x="214" y="196" fill="#666" fontSize="9">2′</text>
    <text x={deoxy ? 206 : 168} y="246" fill={deoxy ? HL : '#666'} fontSize="9" fontWeight={deoxy ? 700 : 400}>3′</text>
    <text x="86" y="250" fill="#666" fontSize="9">5′</text>
  </svg>
);

// ---- Component ------------------------------------------------------------

const ScienceSection = () => {
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
  };

  const cell = { padding: '9px 0', borderBottom: '1px solid #171717', display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '0.86rem' };

  return (
    <section style={{
      padding: '8rem 5%',
      background: '#000000',
      color: '#ffffff',
      borderTop: '1px solid #1a1a1a',
      borderBottom: '1px solid #1a1a1a'
    }}>
      <div className="container">

        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={itemVariants}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <span style={{ color: '#888888', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, fontSize: '0.78rem' }}>
            The Science — In Plain Language
          </span>
          <h2 style={{ fontSize: 'clamp(2.5rem, 5vw, 4.2rem)', marginTop: '0.8rem', marginBottom: '1.2rem', color: '#ffffff', fontWeight: 900, letterSpacing: '-0.04em' }}>
            WHY CORDYCEPS?
          </h2>
          <p style={{ maxWidth: '720px', margin: '0 auto', color: '#888888', fontSize: '1.1rem', lineHeight: 1.7 }}>
            <em>Cordyceps militaris</em> is a bright-orange medicinal mushroom studied for a single small molecule
            it makes in unusual amounts: <strong style={{ color: '#fff' }}>cordycepin</strong>. Here's what that is,
            what researchers have actually found, and exactly what we have — and haven't — tested.
          </p>
        </motion.div>

        {/* ── The molecule ─────────────────────────────────────────── */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={itemVariants}
          style={{
            border: '1px solid #222222', borderRadius: '16px', background: '#060606',
            padding: 'clamp(24px, 4vw, 48px)', marginBottom: '2rem'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.6rem', color: '#fff', marginBottom: '10px' }}>The molecule: cordycepin</h3>
            <p style={{ color: '#888', maxWidth: '620px', margin: '0 auto', lineHeight: 1.65, fontSize: '0.98rem' }}>
              Your body runs on <strong style={{ color: '#fff' }}>adenosine</strong> — a building block of ATP (cellular
              energy) and of DNA/RNA. Cordycepin is almost the same molecule, with <strong style={{ color: '#fff' }}>one
              oxygen missing</strong> at the “3′” position. That tiny difference is why cells can mistake it for adenosine,
              and it's the basis of most cordyceps research.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="mf-float" style={{ height: '250px' }}><Nucleoside deoxy={false} /></div>
              <div style={{ color: '#fff', fontWeight: 700, marginTop: '8px' }}>Adenosine</div>
              <div style={{ color: '#777', fontSize: '0.8rem' }}>the body's own energy / genetic building block</div>
            </div>

            <div style={{ textAlign: 'center', color: '#888' }}>
              <div style={{ fontSize: '2rem', color: '#fff' }}>→</div>
              <div style={{ fontSize: '0.82rem', letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: '6px' }}>
                remove one oxygen
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div className="mf-float" style={{ height: '250px', animationDelay: '1.2s' }}><Nucleoside deoxy={true} /></div>
              <div style={{ color: '#fff', fontWeight: 700, marginTop: '8px' }}>Cordycepin</div>
              <div style={{ color: '#777', fontSize: '0.8rem' }}>3′-deoxyadenosine — made by C. militaris</div>
            </div>
          </div>
        </motion.div>

        {/* ── What research shows ──────────────────────────────────── */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={itemVariants}
          style={{ marginTop: '5rem', marginBottom: '2rem', textAlign: 'center' }}
        >
          <h3 style={{ fontSize: '1.9rem', color: '#fff', marginBottom: '10px' }}>What the research shows</h3>
          <p style={{ color: '#888', maxWidth: '640px', margin: '0 auto', lineHeight: 1.6, fontSize: '0.96rem' }}>
            Real, peer-reviewed studies — with links. Note the evidence level on each: human trials, reviews and
            animal studies are not the same thing, and this is research on cordyceps in general, not on our products.
          </p>
        </motion.div>

        <motion.div
          variants={container} initial="hidden" whileInView="visible" viewport={{ once: true }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '5rem' }}
        >
          {studies.map((s) => (
            <motion.a
              key={s.title} href={s.url} target="_blank" rel="noopener noreferrer"
              variants={itemVariants} className="mono-card"
              style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column' }}
            >
              <span style={{
                alignSelf: 'flex-start', fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: '#bbb', border: '1px solid #333', borderRadius: '4px',
                padding: '3px 8px', marginBottom: '14px'
              }}>{s.level}</span>
              <h4 style={{ fontSize: '1.15rem', color: '#fff', marginBottom: '10px' }}>{s.title}</h4>
              <p style={{ color: '#888', lineHeight: 1.6, fontSize: '0.9rem', margin: '0 0 16px 0', flex: 1 }}>{s.body}</p>
              <span style={{ fontSize: '0.8rem', color: '#fff' }}>
                {s.source} <span style={{ color: '#777' }}>· read paper ↗</span>
              </span>
            </motion.a>
          ))}
        </motion.div>

        {/* ── How we make it (animated process) ────────────────────── */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={itemVariants}
          style={{ marginBottom: '2rem', textAlign: 'center' }}
        >
          <h3 style={{ fontSize: '1.9rem', color: '#fff', marginBottom: '10px' }}>From our room to your cup</h3>
        </motion.div>

        <div style={{ position: 'relative', marginBottom: '4rem' }}>
          {/* animated flow line */}
          <svg viewBox="0 0 1000 20" preserveAspectRatio="none" style={{ position: 'absolute', top: '34px', left: 0, width: '100%', height: '20px', zIndex: 0 }}>
            <line x1="40" y1="10" x2="960" y2="10" stroke="#222" strokeWidth="2" />
            <line x1="40" y1="10" x2="960" y2="10" stroke="#fff" strokeWidth="2" className="mf-dash" opacity="0.5" />
          </svg>
          <motion.div
            variants={container} initial="hidden" whileInView="visible" viewport={{ once: true }}
            style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}
          >
            {processSteps.map((p) => (
              <motion.div key={p.n} variants={itemVariants} style={{ textAlign: 'center' }}>
                <div style={{
                  width: '68px', height: '68px', borderRadius: '50%', margin: '0 auto 16px',
                  border: '1px solid #333', background: '#0a0a0a', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: '1.1rem'
                }}>{p.n}</div>
                <div style={{ color: '#fff', fontWeight: 800, marginBottom: '6px', fontSize: '1.05rem' }}>{p.t}</div>
                <div style={{ color: '#888', fontSize: '0.85rem', lineHeight: 1.5, maxWidth: '220px', margin: '0 auto' }}>{p.d}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* ── What we test (COA available on request) ──────────────── */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={itemVariants}
          style={{ border: '1px solid #222222', borderRadius: '16px', background: '#060606', padding: 'clamp(24px, 4vw, 44px)' }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', marginBottom: '6px' }}>
            <h3 style={{ fontSize: '1.6rem', color: '#fff', margin: 0 }}>What we test — every batch</h3>
            <span style={{ color: '#777', fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Third-party NABL lab</span>
          </div>
          <p style={{ color: '#888', fontSize: '0.9rem', margin: '0 0 28px 0', maxWidth: '640px', lineHeight: 1.6 }}>
            Each batch is sent to an independent NABL-accredited laboratory and screened for heavy metals and microbial
            safety before it ever reaches you. The full Certificate of Analysis is available on request.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '32px' }}>
            <div>
              <h4 style={{ color: '#fff', fontSize: '0.82rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Heavy metals</h4>
              {labTested.heavy.map((k) => (
                <div key={k} style={cell}><span style={{ color: '#aaa' }}>{k}</span><span style={{ color: '#fff', fontWeight: 700 }}>✓</span></div>
              ))}
            </div>
            <div>
              <h4 style={{ color: '#fff', fontSize: '0.82rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Microbiology</h4>
              {labTested.micro.map((k) => (
                <div key={k} style={cell}><span style={{ color: '#aaa' }}>{k}</span><span style={{ color: '#fff', fontWeight: 700 }}>✓</span></div>
              ))}
            </div>
            <div>
              <h4 style={{ color: '#fff', fontSize: '0.82rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Quality</h4>
              {labTested.quality.map((k) => (
                <div key={k} style={cell}><span style={{ color: '#aaa' }}>{k}</span><span style={{ color: '#fff', fontWeight: 700 }}>✓</span></div>
              ))}
            </div>
          </div>

          <div style={{
            marginTop: '24px', padding: '14px 18px', borderRadius: '8px', background: '#0d0d0d',
            border: '1px solid #222', color: '#ccc', fontSize: '0.9rem', display: 'inline-block'
          }}>
            📄 Certificate of Analysis available on request
          </div>

          <div style={{
            marginTop: '24px', padding: '16px 18px', border: '1px dashed #333', borderRadius: '8px',
            background: '#0a0a0a', color: '#999', fontSize: '0.86rem', lineHeight: 1.55
          }}>
            <strong style={{ color: '#fff' }}>Being straight with you:</strong> we have <strong style={{ color: '#fff' }}>not</strong> yet
            done HPLC potency testing (cordycepin / adenosine %), UV purity, pesticide or aflatoxin panels, or any
            human study of our own. Those are planned — we only claim what we can back with a report.
          </div>
        </motion.div>

        {/* Disclaimer */}
        <p style={{ color: '#555', fontSize: '0.78rem', lineHeight: 1.6, marginTop: '2.5rem', textAlign: 'center', maxWidth: '820px', marginLeft: 'auto', marginRight: 'auto' }}>
          Educational information only — not medical advice, and not evaluated by FSSAI or the FDA. Cordyceps products are
          not intended to diagnose, treat, cure or prevent any disease. Linked research concerns Cordyceps / cordycepin in
          general and is not a claim about Cordinal Mushrooms products.
        </p>
      </div>
    </section>
  );
};

export default ScienceSection;
